import { createSlice } from '@reduxjs/toolkit';
import axiosInstance from 'src/utils/axiosInstance';

const initialState = {
  siteDepartments: [],
  totalCount: 0,
  totalPages: 0,
  loading: false,
  error: null,
};

export const siteDepartmentSlice = createSlice({
  name: 'siteDepartment',
  initialState,
  reducers: {
    setSiteDepartments: (state, action) => {
      state.siteDepartments = action.payload;
    },
    setTotalCount: (state, action) => {
      state.totalCount = action.payload;
    },
    setTotalPages: (state, action) => {
      state.totalPages = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
  },
});

export const {
  setSiteDepartments,
  setTotalCount,
  setTotalPages,
  setLoading,
  setError,
} = siteDepartmentSlice.actions;

// Get site departments (paginated, searchable & site filtered)
export const getSiteDepartments = (filters = {}) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    const page = filters?.page || 1;
    const limit = filters?.limit || 10;
    const search = filters?.search || '';
    const site_id = filters?.site_id || filters?.siteId || '';

    const params = { page, limit };
    if (search) params.search = search;
    if (site_id) params.site_id = site_id;

    const response = await axiosInstance.get('/admin/site-department/list', { params });

    if (response.data && response.data.status === true) {
      const result = response.data.data?.result || response.data.data || [];
      const totalCount = response.data.data?.totalCount || result.length;
      const totalPages = response.data.data?.totalPages || 1;
      dispatch(setSiteDepartments(result));
      dispatch(setTotalCount(totalCount));
      dispatch(setTotalPages(totalPages));
      return { status: true, data: result, totalCount, totalPages };
    } else {
      dispatch(setSiteDepartments([]));
      dispatch(setTotalCount(0));
      dispatch(setTotalPages(0));
      const errorMsg = response.data?.message || 'Failed to fetch site departments';
      dispatch(setError(errorMsg));
      return { status: false, message: errorMsg };
    }
  } catch (error) {
    dispatch(setSiteDepartments([]));
    dispatch(setTotalCount(0));
    dispatch(setTotalPages(0));
    const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch site departments';
    dispatch(setError(errorMsg));
    return { status: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

// Get single site department details
export const getSiteDepartmentDetails = (id) => async () => {
  try {
    const response = await axiosInstance.get(`/admin/site-department/getbyId/${id}`);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data };
    } else {
      return { status: false, message: response.data?.message || 'Failed to fetch details' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch details';
    return { status: false, message: errorMsg };
  }
};

// Add new site department
export const addSiteDepartment = (payload) => async () => {
  try {
    const response = await axiosInstance.post('/admin/site-department/add', payload);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to add site department' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to add site department';
    return { status: false, message: errorMsg };
  }
};

// Update site department details
export const updateSiteDepartment = (id, payload) => async () => {
  try {
    const response = await axiosInstance.put(`/admin/site-department/update/${id}`, payload);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to update site department' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to update site department';
    return { status: false, message: errorMsg };
  }
};

// Delete site department
export const deleteSiteDepartment = (id) => async () => {
  try {
    const response = await axiosInstance.delete(`/admin/site-department/delete/${id}`);
    if (response.data && response.data.status === true) {
      return { status: true, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to delete site department' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to delete site department';
    return { status: false, message: errorMsg };
  }
};

export default siteDepartmentSlice.reducer;
